import { useState } from "react";
import { trpc } from "../../../utils/trpc";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshTextarea } from "../base_components/sesh_textarea";
import { BoxItem } from "../box_item";
import classNames from "classnames";
import { RemovingThrobber } from "../../removing_throbber";

export function AdminBlurbManager() {
	const [newDashboardMessage, setNewDashboardMessage] = useState("");
	const [newLandingPageMessage, setNewLandingPageMessage] = useState("");
	const [editingBlurbId, setEditingBlurbId] = useState<number | null>(null);
	const [editingDashboardMessage, setEditingDashboardMessage] = useState("");
	const [editingLandingPageMessage, setEditingLandingPageMessage] =
		useState("");
	const utils = trpc.useContext();
	const blurbsQuery = trpc.blurb.getAllBlurbs.useQuery();
	const createBlurbMutation = trpc.blurb.createBlurb.useMutation({
		onSuccess: () => {
			utils.blurb.getAllBlurbs.invalidate();
			utils.blurb.getActiveBlurb.invalidate();
		},
	});
	const editBlurbMutation = trpc.blurb.editBlurb.useMutation({
		onSuccess: () => {
			utils.blurb.getAllBlurbs.invalidate();
			utils.blurb.getActiveBlurb.invalidate();
			setEditingBlurbId(null);
		},
	});
	const toggleBlurbMutation = trpc.blurb.toggleBlurb.useMutation({
		onSuccess: () => {
			utils.blurb.getAllBlurbs.invalidate();
			utils.blurb.getActiveBlurb.invalidate();
		},
	});
	const deleteBlurbMutation = trpc.blurb.deleteBlurb.useMutation({
		onSuccess: () => {
			utils.blurb.getAllBlurbs.invalidate();
			utils.blurb.getActiveBlurb.invalidate();
		},
	});

	if (!blurbsQuery.isSuccess) {
		return <LoadingThrobber />;
	}

	const handleCreateBlurb = async () => {
		if (!newDashboardMessage.trim()) return;

		try {
			await createBlurbMutation.mutateAsync({
				dashboard_message: newDashboardMessage,
				landing_page_message: newLandingPageMessage.trim() || null,
			});
			setNewDashboardMessage("");
			setNewLandingPageMessage("");
		} catch (error) {
			console.error("Failed to create blurb:", error);
		}
	};

	const handleEditBlurb = async (id: number) => {
		try {
			await editBlurbMutation.mutateAsync({
				blurb_id: id,
				dashboard_message: editingDashboardMessage,
				landing_page_message: editingLandingPageMessage.trim() || null,
			});
		} catch (error) {
			console.error("Failed to edit blurb:", error);
		}
	};

	const startEditing = (blurb: any) => {
		setEditingBlurbId(blurb.id);
		setEditingDashboardMessage(blurb.dashboard_message);
		setEditingLandingPageMessage(blurb.landing_page_message || "");
	};

	const cancelEditing = () => {
		setEditingBlurbId(null);
		setEditingDashboardMessage("");
		setEditingLandingPageMessage("");
	};

	const handleToggleBlurb = async (id: number) => {
		try {
			await toggleBlurbMutation.mutateAsync({ blurb_id: id });
		} catch (error) {
			console.error("Failed to toggle blurb:", error);
		}
	};

	const handleDeleteBlurb = async (id: number) => {
		try {
			await deleteBlurbMutation.mutateAsync({ blurb_id: id });
		} catch (error) {
			console.error("Failed to delete blurb:", error);
		}
	};

	return (
		<div className="flex w-full flex-col gap-4">
			<BoxItem bgColor="semidark">
				<div className="flex w-full flex-col gap-4">
					<div className="font-bold">Create New Blurb</div>
					<div>
						<div className="mb-2 font-medium">
							Dashboard Message (Required)
						</div>
						<SeshTextarea
							value={newDashboardMessage}
							onChange={(e) =>
								setNewDashboardMessage(e.target.value)
							}
							placeholder="Enter dashboard message..."
							rows={3}
						/>
					</div>
					<div>
						<div className="mb-2 font-medium">
							Landing Page Message (Optional)
						</div>
						<SeshTextarea
							value={newLandingPageMessage}
							onChange={(e) =>
								setNewLandingPageMessage(e.target.value)
							}
							placeholder="Enter landing page message..."
							rows={3}
						/>
					</div>
					{(newDashboardMessage || newLandingPageMessage) && (
						<div className="flex flex-col gap-4">
							{newDashboardMessage && (
								<div className="flex flex-col gap-2">
									<div className="text-sm font-medium text-content-secondary">
										Dashboard Preview:
									</div>
									<BoxItem bgColor="dark" className="p-4">
										<div
											className="whitespace-pre-wrap"
											dangerouslySetInnerHTML={{
												__html: newDashboardMessage,
											}}
										/>
									</BoxItem>
								</div>
							)}
							{newLandingPageMessage && (
								<div className="flex flex-col gap-2">
									<div className="text-sm font-medium text-content-secondary">
										Landing Page Preview:
									</div>
									<BoxItem bgColor="dark" className="p-4">
										<div
											className="whitespace-pre-wrap"
											dangerouslySetInnerHTML={{
												__html: newLandingPageMessage,
											}}
										/>
									</BoxItem>
								</div>
							)}
						</div>
					)}
					<div className="ml-auto">
						<SeshPrimaryButton
							onClick={handleCreateBlurb}
							disabled={!newDashboardMessage.trim()}
						>
							Create Blurb
						</SeshPrimaryButton>
					</div>
				</div>
			</BoxItem>

			<div className="flex flex-col gap-2">
				<div className="font-bold">Existing Blurbs</div>
				{blurbsQuery.data.map((blurb) => (
					<BoxItem key={blurb.id} bgColor="semidark">
						<div className="flex flex-col gap-2">
							<div className="flex items-center justify-between gap-2">
								<div className="flex items-center gap-2">
									<div
										className={classNames(
											"h-2 w-2 rounded-full",
											{
												"bg-green-500": blurb.is_active,
												"bg-gray-400": !blurb.is_active,
											}
										)}
									/>
									<div className="text-sm text-content-secondary">
										Created:{" "}
										{new Date(
											blurb.created_at
										).toLocaleDateString()}
									</div>
								</div>
								<div className="flex items-center gap-4">
									{editingBlurbId === blurb.id ? (
										<>
											{editBlurbMutation.isLoading ? (
												<RemovingThrobber />
											) : (
												<button
													onClick={() =>
														handleEditBlurb(
															blurb.id
														)
													}
													className="text-green-500 hover:text-green-600"
												>
													<i className="fas fa-check" />
												</button>
											)}
											<button
												onClick={cancelEditing}
												className="text-content-secondary hover:text-content-primary"
											>
												<i className="fas fa-times" />
											</button>
										</>
									) : (
										<>
											<button
												onClick={() =>
													startEditing(blurb)
												}
												className="text-content-secondary hover:text-content-primary"
											>
												<i className="fas fa-edit" />
											</button>
											<button
												onClick={() =>
													handleToggleBlurb(blurb.id)
												}
												className="text-content-secondary hover:text-content-primary"
											>
												<i
													className={classNames(
														"fas",
														{
															"fa-eye":
																!blurb.is_active,
															"fa-eye-slash":
																blurb.is_active,
														}
													)}
												/>
											</button>
											<button
												onClick={() =>
													handleDeleteBlurb(blurb.id)
												}
												className="text-red-500 hover:text-red-600"
											>
												<i className="fas fa-trash" />
											</button>
										</>
									)}
								</div>
							</div>
							{editingBlurbId === blurb.id ? (
								<div className="flex flex-col gap-4">
									<div>
										<div className="mb-2 font-medium">
											Dashboard Message:
										</div>
										<SeshTextarea
											value={editingDashboardMessage}
											onChange={(e) =>
												setEditingDashboardMessage(
													e.target.value
												)
											}
											placeholder="Enter dashboard message..."
											rows={3}
										/>
									</div>
									<div>
										<div className="mb-2 font-medium">
											Landing Page Message:
										</div>
										<SeshTextarea
											value={editingLandingPageMessage}
											onChange={(e) =>
												setEditingLandingPageMessage(
													e.target.value
												)
											}
											placeholder="Enter landing page message..."
											rows={3}
										/>
									</div>
									{(editingDashboardMessage ||
										editingLandingPageMessage) && (
										<div className="flex flex-col gap-4">
											{editingDashboardMessage && (
												<div className="flex flex-col gap-2">
													<div className="text-sm font-medium text-content-secondary">
														Dashboard Preview:
													</div>
													<BoxItem
														bgColor="dark"
														className="p-4"
													>
														<div
															className="whitespace-pre-wrap"
															dangerouslySetInnerHTML={{
																__html: editingDashboardMessage,
															}}
														/>
													</BoxItem>
												</div>
											)}
											{editingLandingPageMessage && (
												<div className="flex flex-col gap-2">
													<div className="text-sm font-medium text-content-secondary">
														Landing Page Preview:
													</div>
													<BoxItem
														bgColor="dark"
														className="p-4"
													>
														<div
															className="whitespace-pre-wrap"
															dangerouslySetInnerHTML={{
																__html: editingLandingPageMessage,
															}}
														/>
													</BoxItem>
												</div>
											)}
										</div>
									)}
								</div>
							) : (
								<>
									<div>
										<div className="mb-2 font-medium">
											Dashboard Message:
										</div>
										<div className="whitespace-pre-wrap">
											{blurb.dashboard_message}
										</div>
									</div>
									{blurb.landing_page_message && (
										<div>
											<div className="mb-2 font-medium">
												Landing Page Message:
											</div>
											<div className="whitespace-pre-wrap">
												{blurb.landing_page_message}
											</div>
										</div>
									)}
								</>
							)}
						</div>
					</BoxItem>
				))}
			</div>
		</div>
	);
}
